import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messages, type UserFarmImportResult, type UserFarmRow } from '@fruitways/shared';
import { Button, ConfirmDialog, EmptyState, Icon, ListRow, SkeletonRows } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { api } from '@/api/endpoints';
import { refreshAfterAdminChange } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/useToast';
import { useOnline } from '@/hooks/useOnline';
import { readSheetFile, saveSheet, type Cell, type ColumnSpec, type SheetValue } from '@/lib/excel';
import { InlineError, InlineNote, t, useAdminData } from './dataShared';
import '@/screens/Capture/capture.css';

const t2 = messages.admin.userFarms;

type RawRow = { row: number; cells: Record<string, Cell> };

const COLUMNS: ColumnSpec[] = [
  { header: t2.headers[0], key: 'farmName', required: true },
  { header: t2.headers[1], key: 'puc', required: true },
  { header: t2.headers[2], key: 'displayName', required: true },
  { header: t2.headers[3], key: 'username', required: true },
  { header: t2.headers[4], key: 'password', required: false },
  { header: t2.headers[5], key: 'role', required: true },
  { header: t2.headers[6], key: 'active', required: true },
];

type State =
  | { step: 'list' }
  | { step: 'checking'; file: string }
  | { step: 'unreadable'; file: string; reason: 'notExcel' | 'columns' | 'empty' }
  | { step: 'checked'; file: string; raw: RawRow[]; result: UserFarmImportResult };

/**
 * User farms: who captures for each farm and how they sign in. The list is read-only; the file
 * is how it is maintained. Nothing here ever shows or carries a password.
 */
export function UserFarmsScreen() {
  const nav = useNavigate();
  const toast = useToast();
  const { online } = useOnline();
  const { userFarms, loading, loadError } = useAdminData();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ step: 'list' });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = userFarms ?? [];
  const users = new Set(rows.map((r) => r.username)).size;
  const farms = new Set(rows.filter((r) => r.puc !== 'ALL').map((r) => r.puc)).size;

  const send = (raw: RawRow[], commit: boolean) =>
    api.admin.userFarms.import({
      commit,
      rows: raw.map((r) => ({
        row: r.row,
        farmName: r.cells['farmName'] ?? null,
        puc: r.cells['puc'] ?? null,
        displayName: r.cells['displayName'] ?? null,
        username: r.cells['username'] ?? null,
        password: r.cells['password'] ?? null,
        role: r.cells['role'] ?? null,
        active: r.cells['active'] ?? null,
      })),
    });

  /** The file the admin edits: what is in now, with the password column left blank. */
  function fileRows(): SheetValue[][] {
    return rows.map((r: UserFarmRow) => [
      r.farmName,
      r.puc,
      r.displayName,
      r.username,
      '',
      r.role,
      r.active ? 1 : 0,
    ]);
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setState({ step: 'checking', file: file.name });
    const sheet = await readSheetFile(file, COLUMNS);
    if (!sheet.ok) {
      setState({ step: 'unreadable', file: file.name, reason: sheet.reason });
      return;
    }
    try {
      const result = await send(sheet.rows, false);
      setState({ step: 'checked', file: file.name, raw: sheet.rows, result });
    } catch {
      setError(t.data.saveFailed);
      setState({ step: 'list' });
    }
  }

  async function apply() {
    if (state.step !== 'checked') return;
    setBusy(true);
    try {
      await send(state.raw, true);
      await refreshAfterAdminChange();
      toast.show(t2.applied);
      setState({ step: 'list' });
    } catch {
      setError(t.data.saveFailed);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  const checked = state.step === 'checked' ? state.result : null;
  const canApply = Boolean(
    checked && checked.errors.length === 0 && checked.ready.length > 0 && !checked.noActiveAdmin,
  );

  return (
    <AppFrame
      title={t2.title}
      subtitle={messages.header.masterData}
      onBack={() => nav('/data')}
      action={<ProfileMenu />}
      offlineMessage={messages.banner.offlineData}
    >
      <div className="fw-content">
        <span className="fw-secondary">{t2.intro}</span>
        {loadError && <InlineError>{loadError}</InlineError>}
        {error && <InlineError>{error}</InlineError>}

        {/* 1. the file */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div className="fw-rows">
            <ListRow
              leading={<Icon name="document-text-outline" size={24} color="var(--color-primary)" />}
              title={t2.file}
              secondary={t2.downloadHint}
            />
            <div style={{ padding: 'var(--space-1) var(--space-2)' }}>
              <Button
                variant="ghost"
                icon="download-outline"
                disabled={loading}
                onClick={async () => {
                  const data = fileRows();
                  await saveSheet(t2.file, t2.headers, data);
                  toast.show(t2.saved(data.length));
                }}
              >
                {t2.download}
              </Button>
            </div>
            <div style={{ padding: 'var(--space-1) var(--space-2)' }}>
              <input
                ref={input}
                data-testid="user-farms-file"
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <Button
                variant="ghost"
                icon="cloud-upload-outline"
                disabled={!online}
                onClick={() => input.current?.click()}
              >
                {state.step === 'list' ? messages.admin.import.chooseFile : t2.changeFile}
              </Button>
            </div>
          </div>
          <InlineNote>{t2.nothingRemoved}</InlineNote>
        </div>

        {/* 2. what the chosen file would do */}
        {state.step === 'checking' && <SkeletonRows count={2} />}
        {state.step === 'unreadable' && (
          <InlineError>
            {state.reason === 'notExcel'
              ? messages.admin.import.notExcel
              : state.reason === 'empty'
                ? messages.admin.import.emptyFile
                : messages.admin.import.unreadable}
          </InlineError>
        )}
        {checked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="fw-label">{messages.admin.import.step3}</span>
            <div className="fw-rows">
              <ListRow
                leading={
                  <Icon
                    name={canApply ? 'checkmark-circle' : 'alert-circle'}
                    size={24}
                    color={canApply ? 'var(--color-primary)' : 'var(--color-danger)'}
                  />
                }
                title={
                  checked.errors.length
                    ? messages.admin.import.toFix(checked.errors.length)
                    : messages.admin.import.ready(checked.ready.length)
                }
                secondary={t2.newCounts(checked.newUsers, checked.newFarms)}
              />
              {checked.errors.map((e) => (
                <ListRow
                  key={`${e.row}-${e.reason}`}
                  title={messages.admin.import.rowTitle(e.row, e.value)}
                  secondary={t2.reasons[e.reason]}
                />
              ))}
            </div>
            {checked.errors.length === 0 && checked.noActiveAdmin && (
              <InlineError>{t2.noActiveAdmin}</InlineError>
            )}
            {checked.deactivating.length > 0 && (
              <InlineNote>{t2.deactivating(checked.deactivating.length)}</InlineNote>
            )}
          </div>
        )}

        {/* 3. the list as it stands */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="fw-label">{t.data.userFarmCount(users, farms)}</span>
          {loading && <SkeletonRows count={4} />}
          {!loading && rows.length === 0 && (
            <EmptyState icon="person-outline" title={t2.emptyTitle}>
              {t2.emptyBody}
            </EmptyState>
          )}
          {!loading && rows.length > 0 && (
            <div className="fw-table-scroll">
              <div className="fw-rows fw-table--four" role="table" aria-label={t2.title}>
                <div className="fw-table__head" role="row">
                  <span role="columnheader">{t2.columns.farm}</span>
                  <span role="columnheader">{t2.columns.puc}</span>
                  <span role="columnheader">{t2.columns.user}</span>
                  <span role="columnheader">{t2.columns.role}</span>
                </div>
                {rows.map((r) => (
                  <div
                    key={`${r.puc}-${r.username}`}
                    className={`fw-table__row${r.active ? '' : ' fw-table--inactive'}`}
                    role="row"
                  >
                    <span role="cell" className="fw-table__code">
                      {r.farmName}
                    </span>
                    <span role="cell">{r.puc}</span>
                    <span role="cell">{r.username}</span>
                    <span role="cell">{r.active ? r.role : t2.inactive}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {canApply && checked && (
        <div className="fw-actions">
          <Button onClick={() => setConfirming(true)} loading={busy} disabled={!online}>
            {t2.apply(checked.ready.length)}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={t2.title}
        body={t2.nothingRemoved}
        note={
          checked && checked.deactivating.length > 0
            ? t2.deactivating(checked.deactivating.length)
            : undefined
        }
        confirmLabel={checked ? t2.apply(checked.ready.length) : ''}
        cancelLabel={messages.common.cancel}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void apply()}
      />
    </AppFrame>
  );
}
